import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    InputAdornment, List, ListItem, ListItemButton, TextField, Typography,
} from '@mui/material';
import {
    SearchRounded as SearchIcon,
    CreditCardRounded as CardIcon,
    InfoOutlined as InfoIcon,
    UndoRounded as UndoIcon,
    WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import { reservationsAPI, usersAPI, User, MealType } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';
import { M, MEAL_LABELS, formatClock, initials, normalizeFa } from './monitorTokens';
import { MealThumb } from './MonitorParts';
import { PendingPerson, deliveryIdentifiers } from './QueuePanel';

const dialogPaper = {
    sx: { borderRadius: M.radiusCard, bgcolor: M.surface, color: M.text, backgroundImage: 'none', border: `1px solid ${M.borderStrong}`, boxShadow: M.cardShadow },
};

const buttonBase = { fontFamily: M.font, borderRadius: M.radiusControl, height: M.controlHeight, fontWeight: 700 };

// Confirms a delivery picked from the waiting list; it goes through the same scan endpoint as a card
export const DeliveryConfirmDialog: React.FC<{
    person: PendingPerson | null;
    mealType: MealType;
    onCancel: () => void;
    onConfirm: (identifiers: string[]) => void;
}> = ({ person, mealType, onCancel, onConfirm }) => {
    const identifiers = person ? deliveryIdentifiers(person) : [];
    const guest = person?.user.role === 'guest';
    return (
        <Dialog open={Boolean(person)} onClose={onCancel} maxWidth="xs" fullWidth dir="rtl" PaperProps={dialogPaper}>
            {person && (
                <>
                    <DialogTitle sx={{ fontFamily: M.font, fontWeight: 900, pb: 1 }}>تحویل دستی {MEAL_LABELS[mealType]}</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: M.primarySoft, color: M.primaryText, display: 'grid', placeItems: 'center', fontFamily: M.font, fontWeight: 800, fontSize: '1.2rem' }}>
                                {initials(person.user.firstName, person.user.lastName)}
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily: M.font, fontWeight: 800, fontSize: '1.25rem' }}>
                                    {person.user.firstName} {person.user.lastName}
                                </Typography>
                                <Typography sx={{ fontFamily: M.font, color: M.textMuted }}>
                                    {person.user.personnelCode && person.user.personnelCode !== '-' ? `کد پرسنلی ${toPersianNumber(person.user.personnelCode)}` : 'بدون کد پرسنلی'}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {person.meals.map((m, i) => (
                                <Box key={`${m.title}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: M.bg, borderRadius: M.radiusControl }}>
                                    <MealThumb imageUrl={m.imageUrl} title={m.title} size={48} />
                                    <Typography sx={{ fontFamily: M.font, fontWeight: 700, flex: 1 }}>{m.title}</Typography>
                                    <Typography sx={{ fontFamily: M.font, color: M.textMuted }}>{toPersianNumber(1)} عدد</Typography>
                                </Box>
                            ))}
                        </Box>
                        {guest && identifiers.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, p: 1.25, borderRadius: M.radiusControl, bgcolor: M.infoSoft, color: M.infoText }}>
                                <InfoIcon fontSize="small" />
                                <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', color: 'inherit' }}>
                                    هر {toPersianNumber(identifiers.length)} پرس با کد ژتون خودش تحویل ثبت می‌شود.
                                </Typography>
                            </Box>
                        )}
                        {identifiers.length === 0 && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 2, p: 1.25, borderRadius: M.radiusControl, bgcolor: M.warningSoft, color: M.warningText }}>
                                <InfoIcon fontSize="small" />
                                <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', color: 'inherit' }}>
                                    {guest
                                        ? 'این رزرو مهمان هنوز کد ژتون ندارد؛ یک بار از صفحه‌ی «رزرو مهمان» ژتونش را چاپ کنید.'
                                        : 'این رزرو کد پرسنلی یا شماره کارت ندارد و از مانیتور قابل تحویل نیست.'}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                        <Button onClick={onCancel} variant="outlined" sx={{ ...buttonBase, flex: 1, color: M.textMuted, borderColor: M.borderStrong }}>انصراف</Button>
                        {/* no autoFocus: a card scanned while this is open ends with Enter, which would deliver this person */}
                        <Button
                            onClick={() => onConfirm(identifiers)}
                            disabled={identifiers.length === 0}
                            variant="contained"
                            sx={{ ...buttonBase, flex: 2, bgcolor: M.successStrong, color: '#fff', '&:hover': { bgcolor: '#166534' } }}
                        >
                            تحویل غذا
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export interface UndoLine {
    key: string;
    title: string;
    imageUrl?: string | null;
    quantity: number;
    deliveredAt?: string | null;
    reservationIds: string[];
}

export interface UndoTarget {
    heading: string;
    subheading?: string;
    lines: UndoLine[];
}

// Confirms reverting deliveries; the chosen meals become undelivered and can be delivered again
export const UndoDeliveryDialog: React.FC<{
    target: UndoTarget | null;
    onCancel: () => void;
    onConfirm: (reservationIds: string[], lines: UndoLine[]) => Promise<void>;
}> = ({ target, onCancel, onConfirm }) => {
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setSelected(new Set(target?.lines.map((l) => l.key) ?? []));
        setError('');
        setSaving(false);
    }, [target]);

    const chosen = target?.lines.filter((l) => selected.has(l.key)) ?? [];
    const count = chosen.reduce((n, l) => n + l.quantity, 0);

    const toggle = (key: string) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const confirm = async () => {
        if (chosen.length === 0) return;
        setSaving(true);
        setError('');
        try {
            await onConfirm(chosen.flatMap((l) => l.reservationIds), chosen);
        } catch (err: any) {
            setError(err.response?.data?.error || 'لغو تحویل انجام نشد');
            setSaving(false);
        }
    };

    const close = () => { if (!saving) onCancel(); };

    return (
        <Dialog open={Boolean(target)} onClose={close} maxWidth="xs" fullWidth dir="rtl" PaperProps={dialogPaper}>
            {target && (
                <>
                    <DialogTitle sx={{ fontFamily: M.font, fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UndoIcon sx={{ color: M.dangerText }} /> لغو تحویل
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontFamily: M.font, fontWeight: 800, fontSize: '1.2rem' }}>{target.heading}</Typography>
                        {target.subheading && <Typography sx={{ fontFamily: M.font, color: M.textMuted }}>{target.subheading}</Typography>}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                            {target.lines.map((l) => {
                                const on = selected.has(l.key);
                                return (
                                    <Box
                                        key={l.key}
                                        component="label"
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, p: 0.75, minHeight: 60, cursor: 'pointer',
                                            borderRadius: M.radiusControl, bgcolor: M.bg, border: `1px solid ${on ? `${M.danger}88` : 'transparent'}`,
                                        }}
                                    >
                                        <Checkbox
                                            checked={on}
                                            onChange={() => toggle(l.key)}
                                            disabled={target.lines.length === 1}
                                            sx={{ color: M.textFaint, '&.Mui-checked': { color: M.dangerText } }}
                                            inputProps={{ 'aria-label': `لغو تحویل ${l.title}` }}
                                        />
                                        <MealThumb imageUrl={l.imageUrl} title={l.title} size={44} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography noWrap sx={{ fontFamily: M.font, fontWeight: 700 }}>{l.title}</Typography>
                                            {l.deliveredAt && (
                                                <Typography sx={{ fontFamily: M.font, fontSize: '0.8rem', color: M.textFaint }}>تحویل ساعت {formatClock(l.deliveredAt)}</Typography>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontFamily: M.font, fontWeight: 800, color: M.textMuted, px: 1 }}>× {toPersianNumber(l.quantity)}</Typography>
                                    </Box>
                                );
                            })}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, mt: 2, p: 1.25, borderRadius: M.radiusControl, bgcolor: M.warningSoft, color: M.warningText }}>
                            <WarningIcon fontSize="small" />
                            <Typography sx={{ fontFamily: M.font, fontSize: '0.9rem', color: 'inherit' }}>
                                غذاهای انتخاب‌شده دوباره «تحویل‌نشده» ثبت می‌شوند و در آمار و گزارش‌ها تحویل حساب نمی‌شوند. فقط تحویل‌های امروز قابل لغو است.
                            </Typography>
                        </Box>
                        {error && <Typography sx={{ fontFamily: M.font, color: M.dangerText, mt: 1.5 }} role="alert">{error}</Typography>}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                        <Button onClick={close} variant="outlined" sx={{ ...buttonBase, flex: 1, color: M.textMuted, borderColor: M.borderStrong }}>انصراف</Button>
                        {/* no autoFocus: a card scanned while this is open ends with Enter */}
                        <Button
                            onClick={confirm}
                            disabled={count === 0 || saving}
                            variant="contained"
                            sx={{ ...buttonBase, flex: 2, bgcolor: '#b91c1c', color: '#fff', '&:hover': { bgcolor: '#991b1b' } }}
                        >
                            {saving ? <CircularProgress size={22} color="inherit" /> : `لغو تحویل ${toPersianNumber(count)} پرس`}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

// A scanned reader serial that belongs to nobody can be linked to an employee, then delivered
export const CardEnrollDialog: React.FC<{
    serial: string | null;
    onClose: () => void;
    onEnrolled: (serial: string) => void;
}> = ({ serial, onClose, onEnrolled }) => {
    const [staff, setStaff] = useState<User[]>([]);
    const [loadingStaff, setLoadingStaff] = useState(false);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<User | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (serial === null) return;
        setSearch('');
        setSelected(null);
        setError('');
        if (staff.length > 0) return;
        let cancelled = false;
        setLoadingStaff(true);
        usersAPI.getUsers('staff')
            .then((list: User[]) => { if (!cancelled) setStaff(list.filter((u) => u.isActive !== false)); })
            .catch(() => { if (!cancelled) setError('دریافت لیست پرسنل ناموفق بود'); })
            .finally(() => { if (!cancelled) setLoadingStaff(false); });
        return () => { cancelled = true; };
    }, [serial, staff.length]);

    const matches = useMemo(() => {
        const q = normalizeFa(search);
        if (!q) return [];
        return staff
            .filter((u) => normalizeFa(`${u.firstName} ${u.lastName}`).includes(q) || (u.personnelCode ?? '').includes(q))
            .slice(0, 8);
    }, [staff, search]);

    const close = () => { if (!saving) onClose(); };

    const confirm = async () => {
        if (!serial || !selected) return;
        setSaving(true);
        setError('');
        try {
            await reservationsAPI.assignCardSerial(selected.id, serial);
            onEnrolled(serial);
        } catch (err: any) {
            setError(err.response?.data?.error || 'ثبت کارت ناموفق بود');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={serial !== null}
            onClose={close}
            maxWidth="xs"
            fullWidth
            dir="rtl"
            PaperProps={dialogPaper}
            TransitionProps={{ onEntered: () => searchRef.current?.focus() }}
        >
            <DialogTitle sx={{ fontFamily: M.font, fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CardIcon sx={{ color: M.infoText }} /> کارت ناشناس
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontFamily: M.font, color: M.textMuted, mb: 1 }}>
                    این کارت به هیچ پرسنلی متصل نیست. صاحب کارت را انتخاب کنید تا ثبت و غذایش تحویل شود:
                </Typography>
                <Typography dir="ltr" sx={{ fontFamily: M.font, fontWeight: 900, fontSize: '1.4rem', letterSpacing: '2px', textAlign: 'center', mb: 2, py: 1, borderRadius: M.radiusControl, bgcolor: M.bg }}>
                    {serial ? toPersianNumber(serial) : ''}
                </Typography>
                <TextField
                    inputRef={searchRef}
                    fullWidth
                    placeholder="نام، نام خانوادگی یا کد پرسنلی"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
                    inputProps={{ 'aria-label': 'جستجوی صاحب کارت' }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: M.textFaint }} /></InputAdornment>,
                        sx: { height: M.controlHeight, borderRadius: M.radiusControl, fontFamily: M.font, color: M.text, bgcolor: M.bg, '& fieldset': { borderColor: M.borderStrong } },
                    }}
                />
                <List sx={{ mt: 1, maxHeight: 320, overflowY: 'auto' }}>
                    {loadingStaff && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
                    {!loadingStaff && matches.map((u) => (
                        <ListItem key={u.id} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                                selected={selected?.id === u.id}
                                onClick={() => setSelected(u)}
                                sx={{
                                    borderRadius: M.radiusControl, border: '2px solid transparent', gap: 1.5, minHeight: 56,
                                    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: M.successSoft, borderColor: M.success },
                                }}
                            >
                                <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: M.primarySoft, color: M.primaryText, display: 'grid', placeItems: 'center', fontFamily: M.font, fontWeight: 800 }}>
                                    {initials(u.firstName, u.lastName)}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily: M.font, fontWeight: 700 }}>{u.firstName} {u.lastName}</Typography>
                                    {u.personnelCode && <Typography sx={{ fontFamily: M.font, fontSize: '0.85rem', color: M.textMuted }}>کد پرسنلی {toPersianNumber(u.personnelCode)}</Typography>}
                                </Box>
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {!loadingStaff && search.trim() !== '' && matches.length === 0 && (
                        <Typography sx={{ fontFamily: M.font, color: M.textFaint, textAlign: 'center', py: 2 }}>پرسنلی پیدا نشد</Typography>
                    )}
                </List>
                {error && <Typography sx={{ fontFamily: M.font, color: M.dangerText, mt: 1 }}>{error}</Typography>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button onClick={close} variant="outlined" sx={{ ...buttonBase, flex: 1, color: M.textMuted, borderColor: M.borderStrong }}>انصراف</Button>
                <Button
                    onClick={confirm}
                    variant="contained"
                    disabled={!selected || saving}
                    sx={{ ...buttonBase, flex: 2, bgcolor: M.successStrong, color: '#fff', '&:hover': { bgcolor: '#166534' } }}
                >
                    {saving ? <CircularProgress size={22} color="inherit" /> : selected ? `ثبت کارت برای ${selected.firstName} ${selected.lastName}` : 'ابتدا پرسنل را انتخاب کنید'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
